import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UncontrolledReactSVGPanZoom } from 'react-svg-pan-zoom';

interface SvgEditorProps {
  url: string;
  altText?: string;
  width?: number;
  height?: number;
  className?: string;
  svgWidth?: number;
  svgHeight?: number;
  selectedPosition?: { x: number; y: number } | null;
  onPositionSelect?: (position: { x: number; y: number }) => void;
  isInvalid?: boolean;
}

export const SvgEditor: React.FC<SvgEditorProps> = ({
  url,
  altText = 'SVG Map',
  width = 800,
  height = 600,
  className = '',
  svgWidth = 1395.57,
  svgHeight = 1083.06,
  selectedPosition,
  onPositionSelect,
  isInvalid = false
}) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actualSvgWidth, setActualSvgWidth] = useState(svgWidth);
  const [actualSvgHeight, setActualSvgHeight] = useState(svgHeight);
  const viewerRef = useRef<any>(null);

  // Marker styling
  const markerRadius = 10;
  const markerColor = "#ef4444"; // red-500
  const markerStrokeColor = "#dc2626"; // red-600

  // Function to extract SVG dimensions from content
  const extractSvgDimensions = (svgText: string): { width: number; height: number } => {
    try {
      // Try to extract viewBox first
      const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
      if (viewBoxMatch) {
        const [, viewBox] = viewBoxMatch;
        const [, , width, height] = viewBox.split(' ').map(Number);
        return { width, height };
      }
      
      // Try to extract width and height attributes
      const widthMatch = svgText.match(/width=["']([^"']+)["']/);
      const heightMatch = svgText.match(/height=["']([^"']+)["']/);
      
      if (widthMatch && heightMatch) {
        return {
          width: parseFloat(widthMatch[1]),
          height: parseFloat(heightMatch[1])
        };
      }
      
      // Fallback to default values
      return { width: svgWidth, height: svgHeight };
    } catch (err) {
      console.error('Error extracting SVG dimensions:', err);
      return { width: svgWidth, height: svgHeight };
    }
  };

  // Function to extract inner content from SVG
  const extractSvgContent = (svgText: string): string => {
    try {
      // Remove the outer <svg> tag and keep only the inner content
      const innerContent = svgText.replace(/<svg[^>]*>/, '').replace(/<\/svg>$/, '');
      return innerContent.trim();
    } catch (err) {
      console.error('Error parsing SVG content:', err);
      return svgText; // Return original if parsing fails
    }
  };

  // Calculate dimensions based on actual SVG size
  const calculatedWidth = actualSvgWidth / actualSvgHeight * height;
  const scaleFactorMin = Math.min(height / actualSvgHeight, calculatedWidth / actualSvgWidth);

  // Load SVG content from URL
  React.useEffect(() => {
    const loadSvg = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'image/svg+xml, text/plain, */*',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to load SVG: ${response.statusText}`);
        }
        
        const svgText = await response.text();
        
        // Extract dimensions from the SVG content
        const dimensions = extractSvgDimensions(svgText);
        setActualSvgWidth(dimensions.width);
        setActualSvgHeight(dimensions.height);
        
        // Extract inner content (remove outer svg tag)
        const innerContent = extractSvgContent(svgText);
        setSvgContent(innerContent);
      } catch (err) {
        console.error('Error loading SVG:', err);
        setError(err instanceof Error ? err.message : 'Failed to load SVG');
      } finally {
        setIsLoading(false);
      }
    };

    if (url) {
      loadSvg();
    }
  }, [url]);

  // Fit to viewer after SVG loads
  useEffect(() => {
    setTimeout(() => {
      viewerRef.current?.fitToViewer("center", "center");
    }, 100);
  }, [svgContent]);

  const handleMapClick = useCallback((event: any) => {
    if (!onPositionSelect) return;
    
    // Get the current transformation values
    // const currentValue = viewerRef.current?.getValue();
    // if (!currentValue) return;
    
    // // Calculate the click position in SVG coordinates
    // const rect = event.target.getBoundingClientRect();
    // const clickX = event.clientX - rect.left;
    // const clickY = event.clientY - rect.top;
    
    // // Transform click coordinates to SVG coordinates
    // const svgX = (clickX - currentValue.e) / currentValue.a;
    // const svgY = (clickY - currentValue.f) / currentValue.d;
    
    // // Convert to percentage coordinates
    // const xPercent = (svgX / actualSvgWidth) * 100;
    // const yPercent = (svgY / actualSvgHeight) * 100;
    
    // // Ensure coordinates are within bounds
    // const clampedX = Math.max(0, Math.min(100, xPercent));
    // const clampedY = Math.max(0, Math.min(100, yPercent));

    console.log(`x: ${event.x}, y: ${event.y}`);
    
    onPositionSelect({ x: event.x, y: event.y });
  }, [onPositionSelect, actualSvgWidth, actualSvgHeight]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-gray-500">Loading SVG...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-red-500 text-center">
          <p>Error loading SVG</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!svgContent) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: calculatedWidth, height }}>
        <div className="text-gray-500">No SVG content available</div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Instructions */}
      <div className="mb-2 text-xs text-gray-500">
        <p>• Click on the map to select coordinates • Drag to pan • Scroll to zoom</p>
      </div>

      {/* SVG Editor with Border */}
      <div className={`border-2 rounded-lg overflow-hidden bg-gray-100 shadow-lg ${isInvalid ? 'border-red-500' : 'border-gray-300'}`}>
        <UncontrolledReactSVGPanZoom
          ref={viewerRef}
          detectAutoPan={false}
          detectWheel={true}
          preventPanOutside={true}
          width={calculatedWidth}
          height={height}
          background="#ffff"
          miniatureProps={{ 
            position: "none",
            background: "#ffff",
            width: 100,
            height: 100
          }}
          scaleFactorMin={scaleFactorMin}
          scaleFactorMax={10}
          onZoom={currentValue => {
            console.log('onZoom', currentValue);
          }}
          onPan={currentValue => {
            console.log('onPan', currentValue);
          }}
          onClick={handleMapClick}
        >
          <svg viewBox={`0 0 ${actualSvgWidth} ${actualSvgHeight}`}>
            <g dangerouslySetInnerHTML={{ __html: svgContent }} />
            
            {/* Selected Position Marker */}
            {selectedPosition && (
              <g>
                {/* Marker circle */}
                <circle
                  cx={(selectedPosition.x ) }
                  cy={(selectedPosition.y ) }
                  r={markerRadius}
                  fill={markerColor}
                  stroke={markerStrokeColor}
                  strokeWidth="2"
                />
                
              </g>
            )}
          </svg>
        </UncontrolledReactSVGPanZoom>
      </div>

      {/* Selected coordinates display */}
      {selectedPosition && (
        <div className="mt-2 text-xs text-gray-600">
          <p>Selected: X: {selectedPosition.x.toFixed(1)}%, Y: {selectedPosition.y.toFixed(1)}%</p>
        </div>
      )}
    </div>
  );
};

export default SvgEditor; 